import { useEffect, useState } from 'react'

import { onBrandingUpdate } from '../utils/brandingEvents'
import { getAuthToken } from './useAuth'

// Default platform name used for browser tab title
export const DEFAULT_PLATFORM_NAME = 'GiveWise Canada Giving Fund'

// Appends "Giving Fund" to a platform name for the browser tab title, unless already present
export function getBrandedTitle(platformName) {
  const name = platformName || DEFAULT_PLATFORM_NAME
  return name.endsWith('Giving Fund') ? name : `${name} Giving Fund`
}

// Session storage keys for branding persistence
const SESSION_BRANDING_ID_KEY = 'session_branding_id'
const SESSION_BRANDING_SOURCE_KEY = 'session_branding_source' // 'url', 'user', 'subdomain'

function setFavicon(faviconData) {
  try {
    const selectors = [
      'link[rel="icon"]',
      'link[rel="shortcut icon"]',
      'link[rel="apple-touch-icon"]',
      'link[rel*="icon"]',
    ]

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((link) => {
        link.remove()
      })
    })

    const favicon = document.createElement('link')
    favicon.rel = 'icon'
    favicon.type = faviconData.includes('image/png') ? 'image/png' : 'image/x-icon'
    favicon.href = faviconData

    favicon.onerror = () => {
      console.error('Failed to load favicon')
    }

    favicon.onload = () => {
      console.log('Favicon loaded successfully')
    }

    const touchIcon = document.createElement('link')
    touchIcon.rel = 'apple-touch-icon'
    touchIcon.href = faviconData

    touchIcon.onerror = () => {
      console.error('Failed to load touch icon')
    }

    touchIcon.onload = () => {
      console.log('Touch icon loaded successfully')
    }

    document.head.appendChild(favicon)
    document.head.appendChild(touchIcon)

    document.title = `${document.title} `
    setTimeout(() => {
      document.title = document.title.trimEnd()
    }, 100)
  } catch (error) {
    console.error('Error setting favicon:', error)
  }
}

// Helper to clear session branding (call on logout)
export function clearSessionBranding() {
  sessionStorage.removeItem(SESSION_BRANDING_ID_KEY)
  sessionStorage.removeItem(SESSION_BRANDING_SOURCE_KEY)
  localStorage.removeItem('branding')
  localStorage.removeItem('brandingKey')
  console.log('Session branding cleared')
}

export function useBranding() {
  // Don't use cached branding initially - always fetch fresh to detect inactive state
  const [brandingKey, setBrandingKey] = useState(null)
  const [branding, setBranding] = useState(null)

  useEffect(() => {
    const fetchAndUpdateBranding = async () => {
      try {
        // First try to get Instance Branding (new system with multi-method resolution)
        let brandingData = null

        try {
          // Use current origin if REACT_APP_API_HOST is not set (Tailscale mode)
          const apiHost = process.env.REACT_APP_API_HOST || window.location.origin

          // Check for ?ib= URL parameter (instance branding override) - HIGHEST PRIORITY
          const urlParams = new URLSearchParams(window.location.search)
          let brandingIdParam = urlParams.get('ib')

          // Include JWT token if available for user-based branding
          const token = getAuthToken()

          // If URL param provided, store it in session for persistence
          if (brandingIdParam) {
            sessionStorage.setItem(SESSION_BRANDING_ID_KEY, brandingIdParam)
            sessionStorage.setItem(SESSION_BRANDING_SOURCE_KEY, 'url')
            console.log(`Branding ID from URL param: ${brandingIdParam} (stored in session)`)
          } else {
            // No URL param - check session storage
            const sessionBrandingId = sessionStorage.getItem(SESSION_BRANDING_ID_KEY)
            const sessionSource = sessionStorage.getItem(SESSION_BRANDING_SOURCE_KEY)
            // Always honor URL-sourced session branding (highest priority)
            // For non-URL sources, only use when not logged in (let backend resolve user branding)
            if (sessionBrandingId && (sessionSource === 'url' || !token)) {
              brandingIdParam = sessionBrandingId
              console.log(`Using session branding ID: ${brandingIdParam} (source: ${sessionSource})`)
            }
          }

          // Build URL with query parameters for branding resolution
          const resolveParams = new URLSearchParams()
          if (brandingIdParam) resolveParams.set('ib', brandingIdParam)
          resolveParams.set('hostname', window.location.hostname)
          const brandingUrl = `${apiHost}/instance-branding/resolve?${resolveParams.toString()}`

          const headers = token ? { Authorization: `Bearer ${token}` } : {}

          const instanceRes = await fetch(brandingUrl, { headers })

          if (instanceRes.ok) {
            const instanceData = await instanceRes.json()

            // If no branding found at all, clear storage and use defaults
            if (instanceData.id === null) {
              console.log('No branding found - clearing storage and using defaults')
              sessionStorage.removeItem(SESSION_BRANDING_ID_KEY)
              sessionStorage.removeItem(SESSION_BRANDING_SOURCE_KEY)
              localStorage.removeItem('branding')
              localStorage.removeItem('brandingKey')
              setBranding(null)
              setBrandingKey(null)
              return
            }

            // Only check isActive when branding was resolved via default fallback
            // User-associated and URL-specified brandings are allowed even if not globally "active"
            if (instanceData.resolvedVia === 'default' && instanceData.isActive === false) {
              console.log('No active branding - clearing storage and using defaults')
              localStorage.removeItem('branding')
              localStorage.removeItem('brandingKey')
              setBranding(null)
              setBrandingKey(null)
              return
            }

            // Store the resolved branding ID in session (for user-based or subdomain resolution)
            if (!sessionStorage.getItem(SESSION_BRANDING_ID_KEY) && instanceData.id) {
              sessionStorage.setItem(SESSION_BRANDING_ID_KEY, instanceData.id.toString())
              sessionStorage.setItem(SESSION_BRANDING_SOURCE_KEY, instanceData.resolvedVia || 'default')
              console.log(`Stored resolved branding ID: ${instanceData.id} (source: ${instanceData.resolvedVia})`)
            }

            // Helper function to convert relative URLs to absolute
            const toAbsoluteUrl = (url) => {
              if (!url) return null
              if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
                return url
              }
              return `${apiHost}${url.startsWith('/') ? '' : '/'}${url}`
            }

            brandingData = {
              ...instanceData,
              // Map Instance Branding to expected format
              brandingKey: `instance-${instanceData.id}`,
              logo: toAbsoluteUrl(instanceData.logoUrl),
              favicon: toAbsoluteUrl(instanceData.faviconUrl),
              // Feature flags from domainSettings
              features: instanceData.domainSettings || {},
              // Custom styling
              customCss: instanceData.customCss,
              customJs: instanceData.customJs,
              analyticsCode: instanceData.analyticsCode,
            }

            setBranding(brandingData)
            localStorage.setItem('branding', JSON.stringify(brandingData))
            localStorage.setItem('brandingKey', brandingData.brandingKey)
            setBrandingKey(brandingData.brandingKey)

            if (instanceData.faviconUrl) {
              setFavicon(toAbsoluteUrl(instanceData.faviconUrl))
            }

            return
          }
        } catch (instanceErr) {
          // Silently fall back to legacy branding
        }

        // Fallback to old branding system
        console.log('Using legacy branding system...')
        const apiHost = process.env.REACT_APP_API_HOST || window.location.origin
        const res = await fetch(`${apiHost}/branding/activeBrandingKey`)
        const { brandingKey: newKey } = await res.json()

        if (!newKey) throw new Error('No active branding key found')

        localStorage.setItem('brandingKey', newKey)
        setBrandingKey(newKey)

        const brandingRes = await fetch(`${apiHost}/branding/${newKey}/full/en`)
        const data = await brandingRes.json()

        if (!data) {
          console.error('No branding data found')
          return
        }

        const legacyBrandingData = {
          ...data,
          brandingKey: newKey,
          logo: data.logo ? `data:image/png;base64,${data.logo}` : null,
          favicon: data.favicon ? `data:image/x-icon;base64,${data.favicon}` : null,
        }

        setBranding(legacyBrandingData)
        localStorage.setItem('branding', JSON.stringify(legacyBrandingData))

        if (legacyBrandingData.favicon) {
          setFavicon(legacyBrandingData.favicon)
        }
      } catch (err) {
        console.error('Error updating branding:', err)
      }
    }

    fetchAndUpdateBranding()

    const unsubscribe = onBrandingUpdate(fetchAndUpdateBranding)
    return unsubscribe
  }, [])

  const getLabelValue = (key, fallback = '') => {
    // For Instance Branding, check direct properties first, then labels
    if (branding?.[key]) return branding[key]
    return branding?.labels?.[key] || fallback
  }

  const getAssetValue = (key, fallback = '') => branding?.[key] || fallback

  // Helper function to get feature flag values
  const getFeatureFlag = (key, fallback = false) =>
    branding?.features?.[key] ?? branding?.domainSettings?.[key] ?? fallback

  // Helper function to get debug border styles
  const getDebugBorder = (sectionName, color) => {
    const showDebugColors = getFeatureFlag('showDebugColors', false)
    if (!showDebugColors) return {}

    console.log(`Debug border enabled for section: ${sectionName}`)
    return {
      border: `3px solid ${color}`,
      boxShadow: `0 0 10px ${color}`,
    }
  }

  // Helper function to get section background color
  const getSectionColor = (sectionKey, fallback = 'transparent') => branding?.[sectionKey] || fallback

  return {
    ...branding,
    brandingKey,
    getLabelValue,
    getAssetValue,
    getFeatureFlag,
    getDebugBorder,
    getSectionColor,
    clearSessionBranding,
    // Convenient direct access to common properties
    platformName: branding?.platformName || branding?.labels?.platformName || DEFAULT_PLATFORM_NAME,
    primaryColor: branding?.primaryColor || '#64bee5',
    // Color properties
    secondaryColor: branding?.secondaryColor || '#293860',
    accentColor: branding?.accentColor || '#eccb48',
    // Auto-calculated contrasting text colors (defaults when no active branding)
    primaryTextColor: branding?.primaryTextColor || '#1a2e52',
    secondaryTextColor: branding?.secondaryTextColor || '#ffffff',
    accentTextColor: branding?.accentTextColor || '#1a2e52',
  }
}
